/** The 15-minute voice: while it is on, says the time at every 15-minute close of the market
 * (09:30 ... 15:30 IST) on a trading day. The switch is in the header (CandleVoiceToggle); the choice is
 * kept in localStorage and is off until it is switched on. The trading day comes from the server's
 * exchange calendar (`/api/market-day`); if the server cannot say, a weekday is taken to be one.
 */

import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { dueSlot, istDate, spokenTime } from "@/lib/candleVoice";
import { getJson } from "@/lib/http";

export const VOICE_KEY = "nss.voice15.enabled";
const TICK_MS = 1000;
const DAY_REFRESH_MS = 30 * 60_000;

interface MarketDay {
  date: string;
  trading_day: boolean;
}

export function voiceStored(): boolean {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(VOICE_KEY) === "1";
  } catch {
    return false;
  }
}

export function storeVoice(on: boolean): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(VOICE_KEY, on ? "1" : "0");
  } catch {
    // the choice lasts until the page is closed
  }
}

/** Say something with the browser's voice; nothing when there is none. */
export function say(text: string): void {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(new SpeechSynthesisUtterance(text));
}

export function useCandleVoice(enabled: boolean): void {
  const day = useQuery({
    queryKey: ["market-day"],
    queryFn: () => getJson<MarketDay>("/api/market-day"),
    enabled,
    refetchInterval: DAY_REFRESH_MS,
    retry: false,
  });
  const spoken = useRef("");
  const waiting = day.isPending && day.fetchStatus !== "idle"; // the server has not said yet
  const dayData = day.data;
  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setInterval(() => {
      const now = Date.now();
      const slot = dueSlot(now);
      if (slot === null || slot.key === spoken.current || waiting) return;
      // the server's word for today; when it cannot say (or says of another date), a weekday counts
      if (dayData?.date === istDate(now) && !dayData.trading_day) return;
      spoken.current = slot.key;
      say(spokenTime(slot.hour, slot.minute));
    }, TICK_MS);
    return () => {
      clearInterval(timer);
    };
  }, [enabled, waiting, dayData]);
}
